import type { Observable } from 'rxjs';
import type { Uuid } from '@common/types';
import type { Update } from '@ngrx/entity';
import type { NormalizedGridEntity, DenormalizedGridEntity } from '@web/types';

import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';

import { WidgetsFacade } from '@web/store/page-structure/widgets/widgets.facade';

import { gridInstance } from './grids.models';

import GridsActions from './grids.actions';
import * as GridSelectors from './grids.selectors';

@Injectable()
export class GridsFacade {
    private readonly store = inject(Store);
    private readonly widgetsFacade = inject(WidgetsFacade);

    /**
     * Combine pieces of state using createSelector,
     * and expose them as observables through the facade.
     */
    public readonly grids$ = this.store.select(GridSelectors.selectAllGrids);
    public readonly gridsEntities$ = this.store.select(GridSelectors.selectGridsEntities);

    /**
     * Use the initialization action to perform one
     * or more tasks in your Effects.
     */
    public init(entities: NormalizedGridEntity[]): void {
        this.store.dispatch(GridsActions.init({ entities }));
    }

    public add(entity: DenormalizedGridEntity, order: number): void {
        this.store.dispatch(GridsActions.add({ entity, order }));
    }

    public create(parentId: Uuid, variant: number, order: number): void {
        this.store.dispatch(GridsActions.create({ parentId, variant, order }));
    }

    public updateProperties(id: Uuid, changes: Partial<NormalizedGridEntity>): void {
        this.store.dispatch(GridsActions.updateProperties({ update: { id, changes } }));
    }

    public updateManyProperties(updates: Update<NormalizedGridEntity>[]): void {
        this.store.dispatch(GridsActions.updateManyProperties({ updates }));
    }

    public remove(entity: DenormalizedGridEntity, order: number, skipHistory: boolean = false): void {
        this.store.dispatch(GridsActions.remove({ entity, order, skipHistory }));

        this.widgetsFacade.removeManyWidgets(entity.children.flat());
    }

    public copy(entity: DenormalizedGridEntity, order?: number): void {
        this.store.dispatch(GridsActions.copy({ entity, order }));
    }

    public added(entity: NormalizedGridEntity, order?: number): void {
        this.store.dispatch(GridsActions.added({ entity, order }));
    }

    public moveWidgetFromGridToColumn(
        widgetId: Uuid,
        prevGridCellIndex: number,
        prevParentId: Uuid,
        nextParentId: Uuid,
        prevIndex: number,
        nextIndex: number,
        skipHistory: boolean = false
    ): void {
        this.store.dispatch(
            GridsActions.moveWidgetFromGridToColumn({
                widgetId,
                prevGridCellIndex,
                prevParentId,
                nextParentId,
                nextIndex,
                prevIndex,
                skipHistory,
            })
        );
    }

    public moveWidgetFromColumnToGrid(
        widgetId: Uuid,
        nextGridCellIndex: number,
        prevParentId: Uuid,
        nextParentId: Uuid,
        prevIndex: number,
        nextIndex: number,
        skipHistory: boolean = false
    ): void {
        this.store.dispatch(
            GridsActions.moveWidgetFromColumnToGrid({
                widgetId,
                nextGridCellIndex,
                prevParentId,
                nextParentId,
                nextIndex,
                prevIndex,
                skipHistory,
            })
        );
    }

    public moveWidgetFromGridToGrid(
        widgetId: Uuid,
        prevGridCellIndex: number,
        nextGridCellIndex: number,
        prevParentId: Uuid,
        nextParentId: Uuid,
        prevIndex: number,
        nextIndex: number,
        skipHistory: boolean = false
    ): void {
        this.store.dispatch(
            GridsActions.moveWidgetFromGridToGrid({
                widgetId,
                prevParentId,
                nextParentId,
                prevGridCellIndex,
                nextGridCellIndex,
                prevIndex,
                nextIndex,
                skipHistory,
            })
        );
    }

    public getGridInstanceValue(parentId: Uuid, variant: number): NormalizedGridEntity {
        return gridInstance({ parentId, variant });
    }

    public denormalizedById$(id: Uuid): Observable<DenormalizedGridEntity> {
        return this.store.select(GridSelectors.selectDenormalizedGridEntityById(id));
    }

    public byId$(id: Uuid): Observable<NormalizedGridEntity> {
        return this.store.select(GridSelectors.selectGridById(id));
    }
}
